import { useEffect, useState } from 'react';
import { api, formatDate, photoUrl, type PhotoCoords, type Specimen } from '../api.ts';
import { Icon } from './Icon.tsx';

/**
 * Координаты из снимков (ТЗ 7.3).
 *
 * Правила, которые здесь важнее кода:
 *   — предложение появляется один раз и только пока координат нет нигде;
 *   — снимки из домашней зоны не считаются вовсе и не упоминаются;
 *   — расхождение до 200 м — это один выход с разных ракурсов, молчим;
 *   — больше — показываем разбор и запоминаем решение, даже если оно
 *     «не хочу разбираться».
 */
export function CoordHint({
  specimen,
  onChanged,
}: {
  specimen: Specimen;
  onChanged: () => void;
}) {
  const [info, setInfo] = useState<PhotoCoords | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.photoCoords(specimen.id).then(setInfo).catch(() => setInfo(null));
  }, [specimen.id]);

  if (!info || info.candidates.length === 0) return null;
  if (!info.offer && !info.conflicting) return null;
  if (info.dismissed) return null;

  async function use(latitude: number, longitude: number) {
    setBusy(true);
    await api.updateSpecimen(specimen.id, {
      find_latitude: latitude,
      find_longitude: longitude,
      find_coord_source: 'exif',
      // Бытовой GPS телефона — это десятки метров, не метры.
      find_coord_precision_m: 30,
    });
    setBusy(false);
    onChanged();
  }

  async function dismiss() {
    setBusy(true);
    await api.dismissCoordHint(specimen.id);
    setBusy(false);
    setInfo(null);
  }

  // Расхождение больше 200 м: выбор точки, а не предложение (правило 4).
  if (info.conflicting) {
    return (
      <>
        <div className="chips">
          <button
            type="button"
            className="chip chip--warn"
            onClick={() => setExpanded((v) => !v)}
          >
            координаты в снимках различаются · {formatDistance(info.spread_m)}
          </button>
        </div>

        {expanded && (
          <div className="coord-hint">
            <div className="hint" style={{ marginTop: 0 }}>
              Снимки сделаны в разных местах. Выберите тот, что снят на находке.
            </div>
            {info.candidates.map((candidate) => (
              <div key={candidate.photo_id} className="coord-option">
                <img className="label-thumb" src={photoUrl(candidate.photo_id, true)} alt="" />
                <div style={{ minWidth: 0 }}>
                  <div className="mono small">
                    {candidate.latitude.toFixed(5)}, {candidate.longitude.toFixed(5)}
                  </div>
                  <div className="hint">
                    {candidate.taken_at ? formatDate(candidate.taken_at.slice(0, 10)) : 'без даты'}
                  </div>
                </div>
                <div className="spacer" />
                <button
                  type="button"
                  className="chip chip--plain"
                  disabled={busy}
                  onClick={() => void use(candidate.latitude, candidate.longitude)}
                >
                  сюда
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn--full"
              style={{ marginTop: 'var(--space-3)' }}
              disabled={busy}
              onClick={dismiss}
            >
              Не ставить точку и больше не спрашивать
            </button>
          </div>
        )}
      </>
    );
  }

  const first = info.candidates[0]!;
  return (
    <div className="notice notice--ok" style={{ marginTop: 'var(--space-3)' }}>
      <Icon name="pin" className="icon icon-sm" />
      <span style={{ flex: 1 }}>
        В снимках есть координаты: {first.latitude.toFixed(5)}, {first.longitude.toFixed(5)}.
        Поставить их образцу?
        <span className="chips">
          <button
            type="button"
            className="chip"
            disabled={busy}
            onClick={() => void use(first.latitude, first.longitude)}
          >
            поставить
          </button>
          <button type="button" className="chip chip--plain" disabled={busy} onClick={dismiss}>
            не надо
          </button>
        </span>
      </span>
    </div>
  );
}

function formatDistance(meters: number): string {
  return meters >= 1000 ? `${(meters / 1000).toFixed(1)} км` : `${meters} м`;
}
